/**
 * Combo générique, filtrable à la frappe — réutilisé pour Client ET Site dans
 * Le Parc plutôt que d'écrire deux composants presque identiques.
 *
 * Le filtrage se fait sur la liste déjà chargée (`options`) : ce composant
 * n'appelle jamais l'API lui-même, il ne fait que présenter et filtrer ce
 * qu'on lui donne.
 */
import { useEffect, useRef, useState } from 'react';

export interface OptionCombo {
  readonly id: string;
  readonly label: string;
  /** Affiché à droite de l'option, par exemple le nombre de machines. */
  readonly count?: number;
}

export function ComboRecherche({
  options,
  valeur,
  onChange,
  placeholder,
  viderLibelle = 'Tous',
}: {
  options: readonly OptionCombo[];
  valeur: string | null;
  onChange: (id: string | null) => void;
  placeholder: string;
  viderLibelle?: string;
}) {
  const selectionnee = options.find((o) => o.id === valeur) ?? null;
  const [saisie, setSaisie] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const conteneurRef = useRef<HTMLDivElement>(null);

  // La saisie affichée suit la sélection tant qu'on ne tape pas soi-même —
  // sans quoi changer de machine sélectionnée laisserait un texte périmé.
  useEffect(() => {
    if (!ouvert) setSaisie(selectionnee?.label ?? '');
  }, [selectionnee, ouvert]);

  useEffect(() => {
    function surClicExterieur(e: MouseEvent) {
      if (conteneurRef.current && !conteneurRef.current.contains(e.target as Node)) {
        setOuvert(false);
      }
    }
    document.addEventListener('mousedown', surClicExterieur);
    return () => document.removeEventListener('mousedown', surClicExterieur);
  }, []);

  const filtre = saisie.trim().toLowerCase();
  const filtrees =
    filtre === ''
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(filtre));

  const choisir = (id: string | null) => {
    onChange(id);
    setOuvert(false);
  };

  return (
    <div className="combo-recherche" ref={conteneurRef}>
      <div className="combo-recherche-champ">
        <input
          type="text"
          placeholder={placeholder}
          value={ouvert ? saisie : (selectionnee?.label ?? saisie)}
          onFocus={() => {
            setOuvert(true);
            setSaisie('');
          }}
          onChange={(e) => setSaisie(e.target.value)}
        />
        {selectionnee && !ouvert && (
          <button
            type="button"
            className="combo-recherche-vider"
            aria-label="Effacer la sélection"
            onClick={() => choisir(null)}
          >
            ×
          </button>
        )}
      </div>
      {ouvert && (
        <ul className="combo-recherche-liste">
          <li>
            <button type="button" onClick={() => choisir(null)}>
              {viderLibelle}
            </button>
          </li>
          {filtrees.length === 0 ? (
            <li className="combo-recherche-vide">Aucun résultat.</li>
          ) : (
            filtrees.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  className={o.id === valeur ? 'actif' : ''}
                  onClick={() => choisir(o.id)}
                >
                  <span>{o.label}</span>
                  {o.count !== undefined && <span className="aide">{o.count}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
